import { insights } from '@repletoryreach/sdk-api';
import { Button, Group, Text } from '@repletoryreach/sdk-ui';
import { Select, TextInput } from '@repletoryreach/sdk-ui/inputs';
import type { Translate } from '../../../i18n/I18nProvider';
import { formatCount } from '../../../lib/format';
import { OUTCOMES, SENTIMENTS, emptyFilters, type Filters, type Row } from '../lib/data';
import { MUTED, channelLabel, outcomeLabel, sentimentLabel } from '../shared';

/**
 * One row of filters above every tab: dates within the period, channel, topic, kind of customer, outcome and feeling.
 * Every figure, chart and table follows it; the AI's text is written for the whole report and says so.
 */
export function FilterBar({ report, rows, shown, filters, onChange, t }: {
  report: insights.InsightReport; rows: Row[]; shown: number; filters: Filters; onChange: (f: Filters) => void; t: Translate;
}) {
  const set = (patch: Partial<Filters>) => onChange({ ...filters, ...patch });
  const channels = [...new Set(rows.map((r) => r.channel))].sort();
  const topics = [...new Set([...(report.taxonomy?.topics ?? []).map((x) => x.name), ...rows.flatMap((r) => r.topics)])];
  const types = [...new Set([...(report.taxonomy?.customer_types ?? []).map((x) => x.name), ...rows.map((r) => r.customer_type).filter((x): x is string => !!x)])];
  const any = (key: string) => ({ value: '', label: t(key) });
  const clear = () => onChange(emptyFilters(report.from, report.to));

  return (
    <div className="rr-insights__filters" data-testid="insights-filters" role="group" aria-label={t('insights.filter.aria')}>
      <Group gap="sm" wrap="wrap" align="flex-end">
        <TextInput size="xs" type="date" label={t('insights.filter.from')} value={filters.from} min={report.from} max={filters.to}
          onChange={(e) => set({ from: e.currentTarget.value || report.from })} />
        <TextInput size="xs" type="date" label={t('insights.filter.to')} value={filters.to} min={filters.from} max={report.to}
          onChange={(e) => set({ to: e.currentTarget.value || report.to })} />
        <Select size="xs" label={t('insights.filter.channel')} value={filters.channel} onChange={(e) => set({ channel: e.currentTarget.value })}
          data={[any('insights.filter.anyChannel'), ...channels.map((c) => ({ value: c, label: channelLabel(t, c) }))]} data-testid="insights-filter-channel" />
        <Select size="xs" label={t('insights.filter.topic')} value={filters.topic} onChange={(e) => set({ topic: e.currentTarget.value })}
          data={[any('insights.filter.anyTopic'), ...topics.map((x) => ({ value: x, label: x }))]} data-testid="insights-filter-topic" />
        {types.length > 0 && (
          <Select size="xs" label={t('insights.filter.customerType')} value={filters.customerType} onChange={(e) => set({ customerType: e.currentTarget.value })}
            data={[any('insights.filter.anyCustomerType'), ...types.map((x) => ({ value: x, label: x }))]} />
        )}
        <Select size="xs" label={t('insights.filter.outcome')} value={filters.outcome} onChange={(e) => set({ outcome: e.currentTarget.value })}
          data={[any('insights.filter.anyOutcome'), ...OUTCOMES.map((o) => ({ value: o, label: outcomeLabel(t, o) }))]} data-testid="insights-filter-outcome" />
        <Select size="xs" label={t('insights.filter.sentiment')} value={filters.sentiment} onChange={(e) => set({ sentiment: e.currentTarget.value })}
          data={[any('insights.filter.anySentiment'), ...SENTIMENTS.map((s) => ({ value: s, label: sentimentLabel(t, s) }))]} />
      </Group>
      <Group gap="sm" wrap="wrap" style={{ marginTop: 8 }}>
        <Text role="caption" c={MUTED} data-testid="insights-filter-count">
          {t('insights.filter.showing', { shown: formatCount(shown), total: formatCount(rows.length) })}
        </Text>
        {filters.refs.length > 0 && (
          <Text role="caption" fw={600} data-testid="insights-filter-refs">{t('insights.filter.refs', { count: formatCount(filters.refs.length) })}</Text>
        )}
        {(shown !== rows.length || filters.refs.length > 0) && (
          <Button size="xs" variant="subtle" onClick={clear} data-testid="insights-filter-clear">{t('insights.filter.clear')}</Button>
        )}
      </Group>
    </div>
  );
}
